'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarketRecap } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { MarketDetailTabTransition } from '@/components/market/market-detail-tab-transition';
import { ResolutionIntegritySection } from '@/components/market/resolution-integrity/resolution-integrity-section';
import { ResolvedPanel } from '@/components/market/resolved-panel';
import { MarketTradersTabContainer } from '@/components/market/traders/market-traders-tab-container';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Panel } from '@/components/ui/panel';
import { logger } from '@/lib/telemetry/logger';
import type { OnchainTradeData } from '@/queries/onchain-trade-data';
const TABS = ['traders', 'recap'] as const;
type WinnerTab = (typeof TABS)[number];

/**
 * Traders / Recap tabs for the winner token page. Traders is the PnL
 * leaderboard; Recap lazily owns the immutable resolution-snapshot read. The
 * settled debate ("theses") lives on the original market page, reachable via
 * the recap's "View market".
 *
 * Tab height uses the same measured auto-height primitive as market detail so
 * the traders list can grow with the page below `lg` instead of nesting inside
 * a layout-projected box.
 */
export function WinnerTabs(t0) {
  const $ = _c(24);
  const {
    marketPubkey,
    poolSnapshot
  } = t0;
  const t = useTranslations("market.winnerDetail");
  const [tab, setTab] = useState("traders");
  const [animateTabChanges, setAnimateTabChanges] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      if (TABS.includes(next as WinnerTab)) {
        setAnimateTabChanges(true);
        setTab(next as WinnerTab);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("tradersTab");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <TabsTrigger value="traders">{t2}</TabsTrigger>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("recapTab");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <TabsTrigger value="recap">{t4}</TabsTrigger>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="px-4 pt-4"><TabsList variant="segmented" size="md">{t3}{t5}</TabsList></div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== marketPubkey || $[13] !== poolSnapshot || $[14] !== tab) {
    t7 = tab === "traders" ? <MarketTradersTabContainer marketPubkey={marketPubkey} poolSnapshot={poolSnapshot} showOutcomeFilter={false} subscribeAmm={true} /> : <WinnerRecap marketPubkey={marketPubkey} />;
    $[12] = marketPubkey;
    $[13] = poolSnapshot;
    $[14] = tab;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== animateTabChanges || $[17] !== t7 || $[18] !== tab) {
    t8 = <div className="min-w-0 px-4 pb-4"><MarketDetailTabTransition activeTab={tab} animateChanges={animateTabChanges}>{t7}</MarketDetailTabTransition></div>;
    $[16] = animateTabChanges;
    $[17] = t7;
    $[18] = tab;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8 || $[22] !== tab) {
    t9 = <Panel density="compact" className="min-w-0 py-0"><Tabs value={tab} onValueChange={t1} className="gap-4">{t6}{t8}</Tabs></Panel>;
    $[20] = t6;
    $[21] = t8;
    $[22] = tab;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
function WinnerRecap(t0) {
  const $ = _c(25);
  const {
    marketPubkey
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["pubkey", "title", "resolvedAt", "winningOutcomeIndex", "participantCount", "volumeAtResolution", "outcomes"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== marketPubkey) {
    t2 = {
      pubkey: marketPubkey,
      fields: t1
    };
    $[1] = marketPubkey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      fetchOnce: true
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const recapQuery = useGetMarketRecap(t2, t3);
  const recap = recapQuery.data;
  let t4;
  let t5;
  if ($[4] !== marketPubkey || $[5] !== recapQuery.isError) {
    t4 = () => {
      if (!recapQuery.isError) {
        return;
      }
      logger.warn("winner_detail.recap_fetch_failed", {
        marketPubkey
      });
    };
    t5 = [marketPubkey, recapQuery.isError];
    $[4] = marketPubkey;
    $[5] = recapQuery.isError;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  if (recap === undefined) {
    if (recapQuery.isError) {
      let t6;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <p className="text-body text-foreground-secondary">Recap is unavailable.</p>;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      return t6;
    }
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="h-48 animate-pulse rounded-card bg-surface-secondary" />;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  const t6 = recap.pubkey;
  const t7 = recap.title;
  const t8 = recap.winningOutcomeIndex;
  let t9;
  if ($[10] !== recap.outcomes) {
    t9 = recap.outcomes.map(_temp);
    $[10] = recap.outcomes;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== recap.participantCount || $[13] !== recap.pubkey || $[14] !== recap.resolvedAt || $[15] !== recap.title || $[16] !== recap.volumeAtResolution || $[17] !== recap.winningOutcomeIndex || $[18] !== t9) {
    t10 = <ResolvedPanel marketPubkey={t6} marketTitle={t7} marketStatus="graduated" winnerOutcomeIndex={t8} variant="market-tab" outcomes={t9} resolvedAtIso={recap.resolvedAt} volumeAtResolutionRaw={recap.volumeAtResolution} uniqueTradersAtResolution={recap.participantCount} />;
    $[12] = recap.participantCount;
    $[13] = recap.pubkey;
    $[14] = recap.resolvedAt;
    $[15] = recap.title;
    $[16] = recap.volumeAtResolution;
    $[17] = recap.winningOutcomeIndex;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== recap.pubkey) {
    t11 = <ResolutionIntegritySection marketPubkey={recap.pubkey} />;
    $[20] = recap.pubkey;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="flex min-w-0 flex-col gap-4">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function _temp(outcome) {
  return {
    outcomeIndex: outcome.index,
    label: outcome.label,
    ticker: outcome.ticker,
    mint: outcome.mint,
    imageUrl: outcome.imageUrl,
    imageNsfw: outcome.imageNsfw,
    convictionAtResolution: String(outcome.finalConsensusPpb),
    convictionAnchor: null,
    peakAtResolution: String(outcome.peakConsensusPpb),
    convictionPeak: null,
    volumeAtResolutionD6: outcome.volumeAtResolution,
    volumeTotalD6: null
  };
}